import React, { ButtonHTMLAttributes, forwardRef } from 'react';

import classNames from 'classnames';
import { Icon } from './Button/Icon';
import LoadingSpinner from '../components/LoadingSpinner';

/**
 * Props for <Button>.
 */
type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * Primary text.
     */
    readonly text?: string;

    /**
     * Indicates whether the button should be displayed full width. Defaults to false.
     */
    readonly fullWidth?: boolean;

    /**
     * Button size. Defaults to small.
     */
    readonly size?: ButtonSize;

    /**
     * Button theme. Defaults to 'positive'.
     */
    readonly theme?: ButtonTheme;

    /**
     * An optional icon code based on Material Icons.
     */
    readonly icon?: string;

    /**
     * An optional secondary text rendered on the right of the button.
     */
    readonly textSecondary?: string;

    /**
     * Indicates whether the secondary text should be highlighted. Defaults to false.
     */
    readonly highlightSecondaryText?: boolean;

    /**
     * An optional icon that's displayed on the right of the button.
     */
    readonly iconRight?: string;

    /**
     * Shows loading spinner
     */
    readonly isLoading?: boolean;
};

/**
 * Supported button sizes.
 */
export enum ButtonSize {
    SMALL = 'sm',
    MEDIUM = 'md',
    LARGE = 'lg'
}

/**
 * Supported themes.
 */
export enum ButtonTheme {
    POSITIVE = 'positive',
    POSITIVE_INVERT = 'positive-invert',
    NEUTRAL = 'neutral',
    WARNING_INVERT = 'warning-invert',
    DANGER = 'danger',
    DANGER_INVERT = 'danger-invert'
}

export const Button = forwardRef<HTMLButtonElement, Props>(
    (
        {
            text,
            fullWidth = false,
            size = ButtonSize.SMALL,
            theme = ButtonTheme.POSITIVE,
            icon,
            textSecondary,
            highlightSecondaryText = false,
            iconRight,
            className,
            children,
            isLoading,
            ...buttonProps
        },
        ref
    ) => {
        return (
            <button
                className={classNames(
                    'gild',
                    'button',
                    'hover-change',
                    `size-${size}`,
                    `theme-${theme}`,
                    fullWidth && 'is-fullwidth',
                    className
                )}
                ref={ref}
                {...buttonProps}
            >
                {isLoading ? (
                    <LoadingSpinner
                        width={size === ButtonSize.SMALL ? 12 : size === ButtonSize.MEDIUM ? 15 : 17}
                        className="mr-sm fl-left"
                    />
                ) : icon ? (
                    <Icon code={icon} />
                ) : null}

                {text ? <span>{text}</span> : children}
                {textSecondary && (
                    <span className={classNames(highlightSecondaryText ? 'cold-dark-red strong ml-sm' : 'ml-sm')}>
                        {textSecondary}
                    </span>
                )}
                {iconRight && <Icon code={iconRight} />}
            </button>
        );
    }
);
